import React, { useState } from 'react';
import { MagnifyingGlass, Buildings } from '@phosphor-icons/react';
import './ProviderProvisioning.css';

function ProviderProvisioning() {
  const [candidates, setCandidates] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [provisioningItemId, setProvisioningItemId] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const loadCandidates = async () => {
    const term = search.trim();
    if (!term || loading) return;
    setLoading(true);
    setHasSearched(true);
    setLoadError('');
    setCandidates([]);
    try {
      const response = await fetch(`/api/provider-provisioning/candidates?search=${encodeURIComponent(term)}`);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Unable to load provider candidates.');
      setCandidates(result.candidates || []);
    } catch (error) {
      setLoadError(error.message || 'Unable to load provider candidates.');
    } finally {
      setLoading(false);
    }
  };

  const provisionProvider = async (candidate) => {
    setProvisioningItemId(candidate.sharePointItemId);
    setFeedback(null);
    try {
      const response = await fetch('/api/providers/provision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sharePointItemId: candidate.sharePointItemId }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Unable to provision this provider.');
      setCandidates((current) => current.map((item) => item.sharePointItemId === candidate.sharePointItemId
        ? { ...item, ready: false, provisionedProviderId: result.provider.providerId }
        : item));
      setFeedback({
        kind: 'success',
        message: `${result.provider.name} provisioned as provider ${result.provider.providerId}. No invitation was sent.`,
      });
    } catch (error) {
      setFeedback({ kind: 'error', message: error.message || 'Unable to provision this provider.' });
    } finally {
      setProvisioningItemId(null);
    }
  };

  return (
    <div className="provider-dashboard provider-provisioning-page">
      <div className="page-header">
        <div className="provisioning-title">
          <Buildings size={28} weight="regular" aria-hidden="true" />
          <h1>Provision Provider</h1>
        </div>
      </div>

      {feedback && <div className={feedback.kind === 'success' ? 'provisioning-feedback success' : 'provisioning-feedback error'} role={feedback.kind === 'success' ? 'status' : 'alert'}>{feedback.message}</div>}
      {loadError && <div className="provisioning-feedback error" role="alert">{loadError}</div>}

      <form className="provisioning-toolbar" onSubmit={(event) => { event.preventDefault(); loadCandidates(); }}>
        <label htmlFor="provider-candidate-search">Search providers</label>
        <input
          id="provider-candidate-search"
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Name prefix, email, AC code, or SharePoint ID"
        />
        <button type="submit" className="action-button btn-solid-blue" disabled={loading || !search.trim()}>
          <MagnifyingGlass size={20} weight="regular" aria-hidden="true" />
          Search
        </button>
        {hasSearched && <span>{candidates.length} {candidates.length === 1 ? 'candidate' : 'candidates'}</span>}
      </form>

      {loading ? <div className="loading">Loading providers...</div> : hasSearched ? (
        <div className="table-section">
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Admin email</th>
                  <th>Employee AC ID</th>
                  <th>Supplier AC ID</th>
                  <th>SharePoint ID</th>
                  <th>Provisioning</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((candidate) => {
                  const blocked = candidate.provisionedProviderId || !candidate.ready;
                  const status = candidate.provisionedProviderId
                    ? `Provisioned (${candidate.provisionedProviderId})`
                    : candidate.blockedReasons?.length
                      ? candidate.blockedReasons.join('; ')
                      : candidate.missing?.length
                      ? `Missing ${candidate.missing.join(', ')}`
                      : 'Ready';
                  return (
                    <tr key={candidate.sharePointItemId}>
                      <td>{candidate.providerName || 'Name missing'}</td>
                      <td>{candidate.adminEmail || 'Missing'}</td>
                      <td>{candidate.employeeAcId || 'Missing'}</td>
                      <td>{candidate.supplierAcId || 'Not set'}</td>
                      <td>{candidate.sharePointItemId}</td>
                      <td><span className={`status-badge ${blocked ? 'status-default' : 'status-active'}`}>{status}</span></td>
                      <td>
                        <button
                          type="button"
                          className="action-button btn-solid-blue"
                          onClick={() => provisionProvider(candidate)}
                          disabled={blocked || provisioningItemId !== null}
                        >
                          {provisioningItemId === candidate.sharePointItemId ? 'Provisioning...' : 'Provision'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {candidates.length === 0 && !loading && !loadError && (
                  <tr><td colSpan="7" className="provisioning-empty">No matching providers.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ProviderProvisioning;